import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ArrowDown } from "components/icons/ArrowDown";
import { Cross } from "components/icons/Cross";
import { Field, FieldProps, getIn } from "formik";
import React, { ComponentProps, ReactNode, useMemo } from "react";
import Select, {
  ClearIndicatorProps,
  components,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  InputProps,
  MenuProps,
  MultiValueGenericProps,
  NoticeProps,
  OptionProps,
  PlaceholderProps,
  Props,
  SingleValueProps,
  ValueContainerProps,
  MenuListProps,
  GroupHeadingProps,
  MultiValueProps,
} from "react-select";
import CreatableSelect, { CreatableProps } from "react-select/creatable";
import { Checkbox } from "../Checkbox/Checkbox";
import { Label } from "../TextInput/TextInput";
import ToolTip from "components/design/toolTip";
import { Button } from "../Button/Button";
import { EditPencil } from "components/icons/EditPencil";
import { CopyIcon } from "components/icons/CopyIcon";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { Info } from "components/icons/Info";

export type OptionData = {
  value: string;
  label: ReactNode;
  index?: number;
  data?: string;
  indent?: number;
  disableOption?: boolean;
};

const SingleValue = ({ children, ...props }: SingleValueProps<OptionData>) => {
  const { copyToClipboard } = useCopyToClipboard();

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // @ts-ignore
    props.selectProps?.handleEdit?.(props.data);
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    copyToClipboard((props.data.label || "").toString());
  };

  return (
    <components.SingleValue
      {...props}
      className={classNames("t-flex t-gap-1 t-mr-1 t-items-center", {
        "!t-whitespace-normal !t-text-overflow-clip":
          //@ts-ignore
          props.selectProps?.shouldWrapText,
      })}
    >
      <span
        className={classNames("t-text-body-sm t-w-full", {
          "!t-opacity-50": props.selectProps.menuIsOpen,
        })}
      >
        {children}
      </span>
      {/* @ts-ignore */}
      {props.selectProps?.allowCopy && !props.selectProps.menuIsOpen && (
        <Button
          size="xsmall"
          customType="ghost_icon"
          onMouseDown={handleCopyClick}
          type="button"
          disabled={props.isDisabled}
        >
          <CopyIcon className="t-text-text-60" />
        </Button>
      )}
      {/* @ts-ignore */}
      {props.selectProps?.allowEdit && !props.selectProps.menuIsOpen && (
        <Button
          customType="ghost"
          size="xsmall"
          onMouseDown={handleEditClick}
          type="button"
          disabled={props.isDisabled}
        >
          <EditPencil className="t-text-text-60" />
        </Button>
      )}
    </components.SingleValue>
  );
};

const ToolTipElement = ({
  tooltip,
  label,
  children,
}: {
  tooltip: boolean;
  label: ReactNode;
  children: ReactNode;
}) => {
  if (tooltip) {
    return (
      <ConditionalToolTip condition={label}>{children}</ConditionalToolTip>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};

const GroupHeading = <T,>(props: GroupHeadingProps<T>) => (
  <components.GroupHeading
    {...props}
    className="t-bg-neutral-0 t-p-2 t-text-text-60 t-text-body-sm"
  />
);

const Option = ({ children, ...props }: OptionProps<OptionData>) => {
  const handleClick = (e: React.MouseEvent) => {
    if (props.isDisabled) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <components.Option
      {...props}
      isDisabled={props.isDisabled}
      className="[&.select\_\_option--is-focused]:t-bg-surface-lighter-grey [&.select\_\_option--is-selected]:t-bg-purple-0 [&.select\_\_option--is-disabled]:t-bg-neutral-0 [&.select\_\_option--is-disabled]:t-text-subtitle-sm [&.select\_\_option--is-disabled]:t-text-text-100 [&.select\_\_option--is-disabled]:t-cursor-not-allowed [&.select\_\_option--is-disabled]:t-opacity-50"
      getStyles={() => ({
        paddingLeft: props.data.indent ? `${props.data.indent * 1.5}rem` : "0",
      })}
    >
      <ToolTipElement
        label={props.data.data}
        //@ts-ignore
        tooltip={props.selectProps?.tooltip}
      >
        <div
          className={classNames(
            "t-truncate !t-border-none t-px-3 t-py-2.5 t-font-medium t-flex t-gap-2.5",
            {
              "t-cursor-not-allowed": props.isDisabled,
              "t-cursor-pointer ": !props.isDisabled,
            }
          )}
          aria-label={props.data.value}
          onClick={handleClick}
        >
          {props.isMulti && (
            <Checkbox
              name={props.data.value}
              checked={props.isSelected}
              disabled={props.isDisabled}
            />
          )}
          {children}
        </div>
      </ToolTipElement>
    </components.Option>
  );
};

export const MultiValueContainer = ({
  children,
  ...props
}: MultiValueGenericProps<OptionData, boolean, GroupBase<OptionData>> & {
  isDisabled?: boolean;
}) => {
  return (
    <components.MultiValueContainer {...props}>
      <button
        type="button"
        disabled={props.data.isDisabled}
        className={classNames(
          "all:unset t-flex t-gap-1.5 t-items-center t-text-body-sm t-bg-neutral-0 t-rounded t-text-text-60 t-px-2 t-select-none t-h-6",
          {
            "t-cursor-not-allowed": props.data.isDisabled,
            "t-cursor-pointer": !props.data.isDisabled,
            "t-w-max": !(props.selectProps as SelectProps).multiValueShowLimit,
            "t-w-full": (props.selectProps as SelectProps).multiValueShowLimit,
          }
        )}
      >
        {children}
      </button>
    </components.MultiValueContainer>
  );
};

const MenuList = ({ children, ...props }: MenuListProps<OptionData>) => {
  return (
    <components.MenuList {...props}>
      {children}

      {/* @ts-ignore // This is a custom prop sent to Select component as directed in documentation ref: https://react-select.com/components#defining-components */}
      {props.selectProps?.actions && (
        <div className="t-py-2 t-px-3 t-sticky t-bottom-0 t-bg-white t-border-0 t-border-t t-border-neutral-10 t-border-solid">
          {/* @ts-ignore */}
          {props.selectProps?.actions}
        </div>
      )}
    </components.MenuList>
  );
};

const Menu = ({ children, ...props }: MenuProps<OptionData>) => {
  return (
    <components.Menu
      className="primary-border t-rounded-md t-bg-surface !t-z-dropdown t-text-body-sm t-drop-shadow-i-dropdown t-mt-2 t-overflow-auto"
      {...props}
    >
      {children}
    </components.Menu>
  );
};

const Input = (props: InputProps<OptionData>) => {
  return (
    <components.Input
      {...props}
      name={props.selectProps.name}
      id={props.selectProps.name}
      data-testid={props.selectProps.name}
      autoComplete="off"
      className="t-w-8 t-text-body-sm"
    />
  );
};

const Control = ({ children, ...props }: ControlProps<OptionData>) => {
  const selectProps = props.selectProps as Props<
    OptionData,
    boolean,
    GroupBase<OptionData>
  > &
    ComboboxCustomSelectProps;
  return (
    <components.Control
      {...props}
      className={classNames(
        String.raw`[&.select\_\_control--is-focused]:t-border-purple-50 [&.select\_\_control--is-focused]:t-bg-surface t-border t-border-solid t-border-neutral-10 focus:t-border-purple t-px-3 t-rounded t-flex t-items-center t-bg-surface-lighter-grey t-justify-center [&.select\_\_control--is-disabled]:t-text-text-30 t-text-text-100 [&.select\_\_control--is-disabled]:t-bg-neutral-0 hover:t-border-purple-30 hover:t-bg-neutral-0`,
        {
          "!t-min-h-7": selectProps.size === "xsmall",
          "!t-min-h-8": selectProps.size === "small",
          "!t-min-h-10": selectProps.size === "regular",
          "!t-min-h-12": selectProps.size === "large",
          "t-border-red-50": selectProps.type === "error",
          "!t-cursor-pointer": !props.isDisabled && !props.isMulti,
        }
      )}
    >
      {children}
    </components.Control>
  );
};

const ValueContainer = ({
  children,
  ...props
}: ValueContainerProps<OptionData>) => {
  const selectProps = props.selectProps as Props<
    OptionData,
    boolean,
    GroupBase<OptionData>
  > &
    ComboboxCustomSelectProps;
  return (
    <components.ValueContainer
      {...props}
      className={classNames("t-flex t-gap-1", {
        "t-py-0": selectProps.size === "small" || selectProps.size === "xsmall",
        "t-py-1.5":
          selectProps.size !== "small" && selectProps.size !== "xsmall",
        "!t-flex-nowrap": selectProps.multiValueShowLimit,
      })}
    >
      {children}
    </components.ValueContainer>
  );
};

const Placeholder = ({ children, ...props }: PlaceholderProps<OptionData>) => (
  <components.Placeholder
    {...props}
    className={classNames("t-text-body-sm", {
      "t-text-neutral-20": props.selectProps.isDisabled,
      "t-text-text-30": !props.selectProps.isDisabled,
    })}
  >
    {children}
  </components.Placeholder>
);

const NoOptionsMessage = ({ children, ...props }: NoticeProps<OptionData>) => (
  <components.NoOptionsMessage {...props} className="t-p-3 t-px-2">
    {children}
  </components.NoOptionsMessage>
);

export const ClearIndicator = (props: ClearIndicatorProps<OptionData>) =>
  props.selectProps.isClearable ? (
    <components.ClearIndicator {...props}>
      <button type="button" className="all:unset t-mr-1" tabIndex={-1}>
        <Cross color="currentColor" />
      </button>
    </components.ClearIndicator>
  ) : null;

const DropdownIndicator = (props: DropdownIndicatorProps<OptionData>) => (
  <components.DropdownIndicator {...props}>
    <button
      type="button"
      className={classNames(
        "all:unset t-transform t-transition t-duration-300 t-ease-in-out",
        {
          "-t-rotate-180": props.selectProps.menuIsOpen,
        }
      )}
      tabIndex={-1}
    >
      <ArrowDown strokeWidth={"1.3"} color="currentColor" />
    </button>
  </components.DropdownIndicator>
);

const MultiValueCustom = (props: MultiValueProps<OptionData>) => {
  const selectProps = props.selectProps as SelectProps;
  const value = props.getValue();

  const overflow = value
    .slice(selectProps.multiValueShowLimit)
    .map((x) => x.label);

  const length = overflow.length;
  const label = `+ ${length}`;

  if (!selectProps.multiValueShowLimit) {
    return <components.MultiValue className="t-overflow-hidden" {...props} />;
  }

  if (props.index < selectProps.multiValueShowLimit) {
    return <components.MultiValue className="t-overflow-hidden" {...props} />;
  }

  if (props.index === selectProps.multiValueShowLimit) {
    return (
      <ToolTip
        disableHoverableContent
        text={
          <div className="t-flex t-flex-col t-gap-1">
            {value.map((x, index) => (
              <div key={index} className="t-text-left">
                {x.label}
              </div>
            ))}
          </div>
        }
      >
        <span
          className={classNames(
            "all:unset t-flex t-gap-1.5 t-items-center t-text-body-sm t-bg-neutral-0 t-rounded t-text-text-60 t-w-max t-px-2 t-select-none t-h-6 t-shrink-0"
          )}
        >
          {label}
        </span>
      </ToolTip>
    );
  }

  return <></>;
};

export type ComboboxCustomSelectProps = {
  size?: "xsmall" | "small" | "regular" | "large";
  type?: "error";
  multiValueShowLimit?: number;
  shouldWrapText?: boolean;
  handleEdit?: (data: OptionData) => void;
  allowEdit?: boolean;
  allowCopy?: boolean;
};

type SelectProps =
  | ({
      creatable?: false;
      size?: "xsmall" | "small" | "regular" | "large";
      actions?: ReactNode;
      type?: "error";
      label?: ReactNode;
      note?: ReactNode;
      fullWidth?: boolean;
      multiValueShowLimit?: number;
      tooltipText?: string;
      shouldWrapText?: boolean;
      handleEdit?: (data: OptionData) => void;
      allowEdit?: boolean;
      allowCopy?: boolean;
    } & Props<OptionData>)
  | ({
      creatable?: true;
      size?: "xsmall" | "small" | "regular" | "large";
      actions?: ReactNode;
      type?: "error";
      label?: ReactNode;
      note?: ReactNode;
      fullWidth?: boolean;
      multiValueShowLimit?: number;
      tooltipText?: string;
      shouldWrapText?: boolean;
      handleEdit?: (data: OptionData) => void;
      allowEdit?: boolean;
      allowCopy?: boolean;
    } & CreatableProps<OptionData, boolean, GroupBase<OptionData>>);

type ComboboxWithFormProps = SelectProps & {
  block?: boolean;
  children?: ReactNode;
  name: string;
  hideError?: boolean;
  tooltip?: boolean;
  onChange?: SelectProps["onChange"];
  shouldValidateOnChange?: boolean;
};

const ComboboxWithForm = ({
  label,
  tooltipText,
  children,
  shouldValidateOnChange = true,
  ...props
}: ComboboxWithFormProps) => (
  <div
    className={classNames("t-relative", {
      "t-block t-w-full": props.block,
    })}
  >
    {label && (
      <div className="t-flex t-items-center">
        <Label
          className="t-block t-text-wrap t-truncate t-text-text-60 !t-p-0 t-mb-1"
          htmlFor={props.name}
          required={props?.required}
        >
          {label}
        </Label>
        {tooltipText && (
          <div className="t-ml-1 t-pb-1.5">
            <ToolTip text={tooltipText} side="right">
              <span>
                <Info />
              </span>
            </ToolTip>
          </div>
        )}
      </div>
    )}
    <Field {...props}>
      {({
        field,
        form: { errors, setFieldValue, values, touched, setFieldTouched },
      }: FieldProps<string[] | string, Record<string, string>>) => {
        const error = getIn(errors, field.name);
        // Force re-validation only when errored, else defer to the form's validateOnChange
        const shouldValidate = error ? true : undefined;

        const onChange: Props<OptionData>["onChange"] = (option, meta) => {
          setFieldTouched(props.name, true, false);
          props.onChange?.(option, meta);
          if (option instanceof Array) {
            setFieldValue(
              props.name,
              option.map((v) => v.value),
              shouldValidate
            );
            return;
          }

          setFieldValue(props.name, option?.value, shouldValidate);
        };

        const isTouched = getIn(touched, field.name);

        return (
          <>
            <BareCombobox
              {...props}
              onChange={onChange}
              type={props?.type || (error && isTouched && "error")}
            />

            {!props.hideError && error && isTouched && (
              <div className="t-mt-1.5 t-text-body-sm t-text-red t-italic t-font-light">
                {error}
              </div>
            )}
          </>
        );
      }}
    </Field>
  </div>
);
const BareCombobox = ({
  size = "regular",
  isClearable = true,
  label,
  tooltipText,
  note,
  ...props
}: SelectProps) => {
  const SelectComponent = props.creatable ? CreatableSelect : Select;

  return (
    <div
      className={classNames(`t-group/${size}`, {
        "t-cursor-not-allowed": props.isDisabled,
        "t-w-full": props.fullWidth,
      })}
    >
      {label && (
        <div className="t-flex t-items-center">
          <Label
            className="t-block t-truncate t-text-text-60 !t-p-0 t-mb-1"
            htmlFor={props.name}
            required={props?.required}
          >
            {label}
          </Label>
          {tooltipText && (
            <div className="t-ml-1 t-pb-1.5">
              <ToolTip text={tooltipText} side="right">
                <span className="t-flex">
                  <Info size="12" />
                </span>
              </ToolTip>
            </div>
          )}
        </div>
      )}

      <SelectComponent
        {...props}
        placeholder={props.placeholder ?? "Select"}
        // @ts-ignore
        size={size}
        classNames={{
          menuPortal: () => "!t-z-dropdown t-pointer-events-auto",
          ...props.classNames,
        }}
        components={{
          SingleValue,
          Option,
          MenuList,
          MultiValueContainer,
          Control,
          Input,
          Menu,
          ValueContainer,
          Placeholder,
          ClearIndicator,
          DropdownIndicator,
          NoOptionsMessage,
          GroupHeading,
          MultiValue: MultiValueCustom,
          ...props.components,
        }}
        isClearable={isClearable}
        closeMenuOnSelect={!props.isMulti}
        hideSelectedOptions={false}
        unstyled
        classNamePrefix="select"
        backspaceRemovesValue={false}
        escapeClearsValue={false}
      />
      {note && (
        <span className="t-text-body-sm t-text-i-neutral-50">{note}</span>
      )}
    </div>
  );
};

export type ComboboxProps =
  | (SelectProps & {
      withForm?: false;
    })
  | (SelectProps &
      ComboboxWithFormProps & {
        withForm: true;
      });

export const Combobox = ({
  size = "regular",
  creatable = false,
  ...props
}: ComboboxProps) => {
  if (props.withForm) {
    return <ComboboxWithForm creatable={creatable} {...props} size={size} />;
  }

  return <BareCombobox creatable={creatable} {...props} size={size} />;
};
